/**
 * Shared rules of the join / sign-up pages (owner 2026-10-25, branch claude/team-onboarding):
 * team invitation page, new firm "set up my account" page, firm setup steps and the welcome window.
 */

export type PasswordChecks = {
  length: boolean;
  lower: boolean;
  upper: boolean;
  number: boolean;
  symbol: boolean;
};

export const passwordChecks = (password: string): PasswordChecks => ({
  length: password.length >= 8 && password.length <= 64,
  lower: /[a-z]/.test(password),
  upper: /[A-Z]/.test(password),
  number: /[0-9]/.test(password),
  symbol: /[^A-Za-z0-9]/.test(password),
});

/** Same rule as the sign-in service (and the server): 8-64 characters and 3 of the 4 kinds. */
export const passwordOk = (password: string) => {
  const c = passwordChecks(password);
  return (
    c.length &&
    [c.lower, c.upper, c.number, c.symbol].filter(Boolean).length >= 3
  );
};

/** 0-4 bars for the strength meter. */
export const passwordStrength = (password: string) => {
  if (!password) return 0;
  const c = passwordChecks(password);
  const kinds = [c.lower, c.upper, c.number, c.symbol].filter(Boolean).length;
  if (!c.length) return 1;
  if (kinds < 3) return 1;
  if (kinds === 3) return password.length >= 12 ? 4 : 3;
  return password.length >= 10 ? 4 : 3;
};

export const strengthLabel = (bars: number) =>
  bars >= 4
    ? "Strong password"
    : bars === 3
    ? "Good password"
    : bars > 0
    ? "Not strong enough yet"
    : "";

/** Why the button can't be used yet (null = fine). */
export const passwordProblem = (password: string, confirm: string) => {
  if (!password) return "Choose a password.";
  if (!passwordOk(password))
    return "Use at least 8 characters and three of: a capital letter, a small letter, a number, a symbol.";
  if (password !== confirm) return "The two passwords don't match.";
  return null;
};

// ---- Welcome window after the first sign-in -------------------------------------------------

export const WELCOME_KEY = "proc_welcome";

export type WelcomeNote =
  | { kind: "member"; email: string; firmName: string; at: number }
  | { kind: "owner"; firmName: string; invited: number; at: number };

const MAX_AGE = 30 * 24 * 60 * 60 * 1000;

export const saveWelcome = (note: WelcomeNote) => {
  try {
    localStorage.setItem(WELCOME_KEY, JSON.stringify(note));
  } catch {
    // private mode: no welcome window, nothing else breaks
  }
};

export const readWelcome = (
  raw: string | null,
  now = Date.now(),
): WelcomeNote | null => {
  if (!raw) return null;
  try {
    const note = JSON.parse(raw) as WelcomeNote;
    if (!note || typeof note.at !== "number" || now - note.at > MAX_AGE)
      return null;
    if (note.kind === "member" && typeof note.email === "string") return note;
    if (note.kind === "owner") return note;
    return null;
  } catch {
    return null;
  }
};

/** Show it to this signed-in person? (A member's note only for the email that accepted.) */
export const welcomeFor = (
  note: WelcomeNote | null,
  signedInEmail?: string | null,
) => {
  if (!note) return false;
  if (note.kind === "owner") return true;
  return (
    !!signedInEmail &&
    note.email.trim().toLowerCase() === signedInEmail.trim().toLowerCase()
  );
};

export const clearWelcome = () => {
  try {
    localStorage.removeItem(WELCOME_KEY);
  } catch {
    // ignore
  }
};

/** "Joe and 11 others", "Joe", "12 people" for the invitation page. */
export const teamLine = (count: number, firmName: string) =>
  count <= 0
    ? `Your team at ${firmName} is waiting for you.`
    : count === 1
    ? `1 person at ${firmName} is waiting for you.`
    : `${count} people at ${firmName} are waiting for you.`;
